import React, { useState, useRef, useEffect } from 'react';
import Fab from '@material-ui/core/Fab';
import Drawer from '@material-ui/core/Drawer';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import ChatIcon from '@material-ui/icons/Chat';
import SendIcon from '@material-ui/icons/Send';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { useAPI } from 'common/API';
import ReactMarkdown from 'react-markdown';
import './ChatWidget.css';
import { Tooltip } from '@material-ui/core';

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const stored = sessionStorage.getItem('chat_messages');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [sessionId, setSessionId] = useState(() => {
    return sessionStorage.getItem('chat_session_id') || null;
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const api = useAPI();
  const bottomRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    try {
      sessionStorage.setItem('chat_messages', JSON.stringify(messages));
    } catch {
      // sessionStorage full or unavailable — ignore
    }
  }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: 'user', content: input.trim() };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput('');
    setLoading(true);
    try {
      const resp = await api.post('api/v1/chat/', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: next, session_id: sessionId }),
      });
      const data = await resp.json();
      if (data.session_id) {
        setSessionId(data.session_id);
        sessionStorage.setItem('chat_session_id', data.session_id);
      }
      setMessages(m => [...m, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      let msg;
      if (err?.response?.status === 429) {
        msg = 'Too many messages. Please wait a minute and try again.';
      } else if (err?.response?.data?.error) {
        msg = err.response.data.error;
      } else {
        msg = 'Ooops… I have a problem. Please contact with support: support@ezcloudllc.com';
      }
      setMessages(m => [...m, { role: 'assistant', content: msg }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <Fab
        color="primary"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}
        onClick={() => setOpen(o => !o)}
      >
        <ChatIcon />
      </Fab>
      <Drawer
        anchor="right"
        open={open}
        variant="persistent"
        style={{ zIndex: 1400 }}
        PaperProps={{ style: { width: 380, display: 'flex', flexDirection: 'column', zIndex: 1400, top: 64, height: 'calc(100vh - 64px)' } }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          p={2}
          bgcolor="primary.main"
          color="white"
          style={{ flexShrink: 0 }}
        >
          <Typography variant="h6">AI Help</Typography>
          <Box>
            <Tooltip title="New chat" placement="top">
              <IconButton size="small" onClick={() => { setMessages([]); setSessionId(null); sessionStorage.removeItem('chat_messages'); sessionStorage.removeItem('chat_session_id'); }} style={{ color: 'white' }}>
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close" placement="top">
              <IconButton size="small" onClick={() => setOpen(false)} style={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Tooltip> 
          </Box>
        </Box>

        <Box
          p={2}
          style={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }}
        >
          {messages.length === 0 && (
            <Typography variant="body2" color="textSecondary" align="center" style={{ marginTop: 16 }}>
              Hi 👋 How can I help you today?
            </Typography>
          )}
          {messages.map((msg, i) => (
            <Box
              key={i}
              display="flex"
              justifyContent={msg.role === 'user' ? 'flex-end' : 'flex-start'}
              mb={1}
            >
              <Paper
                style={{
                  padding: '8px 12px',
                  maxWidth: '80%',
                  background: msg.role === 'user' ? '#1976d2' : '#f5f5f5',
                }}
              >
                {msg.role === 'user' ? (
                  <Typography
                    variant="body2"
                    style={{ color: 'white', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {msg.content}
                  </Typography>
                ) : (
                  <div style={{ fontSize: 14, lineHeight: 1.5, wordBreak: 'break-word' }} className="chat-markdown">
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </div>
                )}
              </Paper>
            </Box>
          ))}
          {loading && (
            <Box display="flex" justifyContent="flex-start" mb={1}>
              <div className="typing-indicator" aria-label="AI Help is typing" role="status">
                <span /><span /><span />
              </div>
            </Box>
          )}
          <div ref={bottomRef} />
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          p={1}
          style={{ borderTop: '1px solid #e0e0e0', flexShrink: 0 }}
        >
          <Box display="flex" alignItems="flex-end">
            <TextField
              fullWidth
              multiline
              rowsMax={4}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Send a message..."
              variant="outlined"
              size="small"
              error={input.length > 2000}
            />
            <IconButton
              onClick={send}
              disabled={loading || !input.trim() || input.length > 2000}
              color="primary"
              style={{ marginLeft: 4 }}
            >
              <SendIcon />
            </IconButton>
          </Box>
          {input.length > 1800 && (
            <Typography
              variant="caption"
              style={{ color: input.length > 2000 ? '#f44336' : '#757575', textAlign: 'right', marginTop: 2 }}
            >
              {input.length}/2000
            </Typography>
          )}
        </Box>
      </Drawer>
    </>
  );
}
